import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, Tooltip, CircularProgress, Avatar, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Snackbar, Alert, Select, MenuItem, FormControl, InputLabel,
  Switch, FormControlLabel, Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  Notifications as NotificationsIcon,
  PhoneAndroid as PhoneIcon,
  FilterList as FilterIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Send as SendIcon,
  Apple as AppleIcon,
  SystemUpdate as UpdateIcon,
  Language as WebIcon
} from '@mui/icons-material';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import AdminNotificationService from './services/AdminNotificationService';

const AppVersions = () => {
  const [users, setUsers]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [filterVersion, setFilterVersion] = useState('');
  const [notifDialog, setNotifDialog]   = useState({ open: false, user: null });
  const [notifForm, setNotifForm]   = useState({
    title: '', body: '', isUpdate: false,
    iosUrl: '', androidUrl: ''
  });
  const [sending, setSending]       = useState(false);
  const [snackbar, setSnackbar]     = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const snap = await getDocs(collection(db, 'users'));
        setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const NO_VERSION = 'Desactualizada';

  // Agrupar por versión (sin appVersion → 'Desactualizada')
  const versionGroups = users.reduce((acc, u) => {
    const v = u.appVersion || NO_VERSION;
    acc[v] = (acc[v] || 0) + 1;
    return acc;
  }, {});

  // Ordenar: versiones reales desc → 'Desactualizada' al final
  const versionList = Object.entries(versionGroups).sort(([a], [b]) => {
    if (a === NO_VERSION) return 1;
    if (b === NO_VERSION) return -1;
    return b.localeCompare(a, undefined, { numeric: true });
  });

  const latestVersion = versionList.find(([v]) => v !== NO_VERSION)?.[0] || '';

  // Usuarios filtrados
  const filtered = users.filter(u => {
    const term = search.toLowerCase();
    const matchSearch = !search ||
      u.nombre?.toLowerCase().includes(term) ||
      u.apellidos?.toLowerCase().includes(term) ||
      u.email?.toLowerCase().includes(term);
    const v = u.appVersion || NO_VERSION;
    const matchVersion = !filterVersion || v === filterVersion;
    return matchSearch && matchVersion;
  }).sort((a, b) => {
    const va = a.appVersion || '';
    const vb = b.appVersion || '';
    // Sin versión va al final
    if (!va && vb) return 1;
    if (va && !vb) return -1;
    return vb.localeCompare(va, undefined, { numeric: true });
  });

  const hasActiveTokens = (user) =>
    (user.expoTokens || []).some(t => t.active && t.token);

  const openNotif = (user) => {
    setNotifForm({ title: '', body: '', isUpdate: false, iosUrl: '', androidUrl: '' });
    setNotifDialog({ open: true, user });
  };

  const handleSend = async () => {
    const { user } = notifDialog;
    const tokens = (user.expoTokens || []).filter(t => t.active && t.token);

    if (!notifForm.title.trim()) {
      setSnackbar({ open: true, message: 'El título es obligatorio', severity: 'warning' });
      return;
    }
    if (tokens.length === 0) {
      setSnackbar({ open: true, message: 'Este usuario no tiene tokens de notificación activos', severity: 'warning' });
      return;
    }

    setSending(true);
    try {
      const base = { título: notifForm.title, descripción: notifForm.body, tipo: 'general', id: `manual_${Date.now()}` };

      if (notifForm.isUpdate && (notifForm.iosUrl || notifForm.androidUrl)) {
        // Separar tokens por plataforma y enviar con el enlace correspondiente
        const iosTokens     = tokens.filter(t => t.deviceInfo?.platform === 'ios');
        const androidTokens = tokens.filter(t => t.deviceInfo?.platform === 'android');
        const otherTokens   = tokens.filter(t => !t.deviceInfo?.platform);

        const sends = [];
        if (iosTokens.length > 0 && notifForm.iosUrl)
          sends.push(AdminNotificationService.sendExpoPushNotifications(iosTokens,     { ...base, enlaces: [{ url: notifForm.iosUrl,     label: 'Actualizar en App Store' }] }));
        if (androidTokens.length > 0 && notifForm.androidUrl)
          sends.push(AdminNotificationService.sendExpoPushNotifications(androidTokens, { ...base, enlaces: [{ url: notifForm.androidUrl, label: 'Actualizar en Google Play' }] }));
        if (otherTokens.length > 0)
          sends.push(AdminNotificationService.sendExpoPushNotifications(otherTokens,   base));

        await Promise.all(sends);
      } else {
        await AdminNotificationService.sendExpoPushNotifications(tokens, base);
      }

      setSnackbar({ open: true, message: `Notificación enviada a ${user.nombre || user.email}`, severity: 'success' });
      setNotifDialog({ open: false, user: null });
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Error al enviar la notificación', severity: 'error' });
    } finally {
      setSending(false);
    }
  };

  const versionColor = (v) => {
    if (!v || v === NO_VERSION) return 'error';
    if (v === latestVersion) return 'success';
    return 'warning';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>

      {/* ── Cabecera ── */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>Versiones de la App</Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          {users.length} usuarios · {versionList.length} versiones distintas
        </Typography>
      </Box>

      {/* ── Stat cards por versión ── */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)', lg: 'repeat(6, 1fr)' },
        gap: 2, mb: 3
      }}>
        {versionList.map(([version, count]) => (
          <Paper
            key={version}
            onClick={() => setFilterVersion(filterVersion === version ? '' : version)}
            sx={{
              p: 2, textAlign: 'center', cursor: 'pointer', borderRadius: 2,
              border: '2px solid',
              borderColor: filterVersion === version ? 'primary.main' : 'transparent',
              transition: 'all .2s',
              '&:hover': { boxShadow: 3, transform: 'translateY(-2px)' }
            }}
          >
            <Typography variant="h4" fontWeight={800}
              color={
                version === NO_VERSION ? 'error.main' :
                version === latestVersion ? 'success.main' : 'warning.main'
              }>
              {count}
            </Typography>
            <Typography variant="caption" fontWeight={600} display="block" noWrap>
              {version}
            </Typography>
            {version === latestVersion && (
              <Chip label="última" size="small" color="success" sx={{ mt: 0.5, height: 18, fontSize: 10 }} />
            )}
          </Paper>
        ))}
      </Box>

      {/* ── Buscador + filtro ── */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            fullWidth size="small"
            placeholder="Buscar por nombre o email…"
            value={search} onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment> }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Filtrar versión</InputLabel>
            <Select
              value={filterVersion}
              label="Filtrar versión"
              onChange={e => setFilterVersion(e.target.value)}
            >
              <MenuItem value="">Todas</MenuItem>
              {versionList.map(([v]) => (
                <MenuItem key={v} value={v}>{v}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* ── Tabla de usuarios ── */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 700 }}>Usuario</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Plataforma</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Versión</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Tokens</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Notificación</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.disabled' }}>
                    Sin resultados
                  </TableCell>
                </TableRow>
              ) : filtered.map(user => {
                const allTokens    = user.expoTokens || [];
                const activeTokens = allTokens.filter(t => t.active && t.token).length;
                const hasIos       = allTokens.some(t => t.active && t.deviceInfo?.platform === 'ios');
                const hasAndroid   = allTokens.some(t => t.active && t.deviceInfo?.platform === 'android');
                const isWebOnly    = !hasIos && !hasAndroid;
                const v = user.appVersion || NO_VERSION;
                return (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar sx={{ width: 32, height: 32, fontSize: 13, bgcolor: 'primary.100', color: 'primary.dark' }}>
                          {(user.nombre || user.email || '?')[0].toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {user.nombre} {user.apellidos || ''}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" justifyContent="center" spacing={0.5}>
                        {hasIos && (
                          <Tooltip title="iOS / iPhone">
                            <AppleIcon sx={{ fontSize: 18, color: '#555' }} />
                          </Tooltip>
                        )}
                        {hasAndroid && (
                          <Tooltip title="Android">
                            <PhoneIcon sx={{ fontSize: 18, color: '#3ddc84' }} />
                          </Tooltip>
                        )}
                        {isWebOnly && (
                          <Tooltip title="Web / sin app">
                            <WebIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={v}
                        size="small"
                        color={versionColor(v)}
                        variant={v === latestVersion ? 'filled' : 'outlined'}
                        sx={{ fontWeight: 600, fontSize: 11 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                        {activeTokens > 0
                          ? <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />
                          : <CancelIcon color="disabled" sx={{ fontSize: 16 }} />
                        }
                        <Typography variant="caption" color={activeTokens > 0 ? 'success.main' : 'text.disabled'}>
                          {activeTokens}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={activeTokens === 0 ? 'Sin tokens activos' : 'Enviar notificación'}>
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            disabled={activeTokens === 0}
                            onClick={() => openNotif(user)}
                          >
                            <NotificationsIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ── Diálogo enviar notificación ── */}
      <Dialog open={notifDialog.open} onClose={() => setNotifDialog({ open: false, user: null })}
        maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <SendIcon color="primary" />
            <Box>
              <Typography variant="h6" fontWeight={700}>Enviar notificación</Typography>
              <Typography variant="caption" color="text.secondary">
                Para: {notifDialog.user?.nombre} {notifDialog.user?.apellidos || ''} · {notifDialog.user?.email}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2.5}>

            {/* Toggle: notificación de actualización */}
            <Box sx={{
              p: 1.5, borderRadius: 1.5, border: '1px solid',
              borderColor: notifForm.isUpdate ? 'primary.300' : 'divider',
              bgcolor: notifForm.isUpdate ? 'primary.50' : 'transparent'
            }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notifForm.isUpdate}
                    onChange={e => {
                      const on = e.target.checked;
                      setNotifForm(p => ({
                        ...p,
                        isUpdate: on,
                        title: on ? '📲 Actualiza la app AMPA' : p.title,
                        body: on ? 'Hay una nueva versión disponible. Actualízala para seguir disfrutando de todas las funcionalidades.' : p.body
                      }));
                    }}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>Notificación de actualización</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Incluye el enlace a la tienda según la plataforma del usuario
                    </Typography>
                  </Box>
                }
              />
            </Box>

            <TextField
              fullWidth
              label="Título"
              value={notifForm.title}
              onChange={e => setNotifForm(p => ({ ...p, title: e.target.value }))}
              placeholder="Ej: Actualiza la app AMPA"
              required
            />
            <TextField
              fullWidth
              label="Mensaje"
              value={notifForm.body}
              onChange={e => setNotifForm(p => ({ ...p, body: e.target.value }))}
              multiline rows={2}
              placeholder="Escribe el cuerpo del mensaje…"
            />

            {/* URLs de tiendas — solo cuando es notificación de actualización */}
            {notifForm.isUpdate && (
              <>
                <Divider><Typography variant="caption" color="text.secondary">ENLACES A TIENDAS</Typography></Divider>
                <TextField
                  fullWidth size="small"
                  label="App Store (iOS)"
                  value={notifForm.iosUrl}
                  onChange={e => setNotifForm(p => ({ ...p, iosUrl: e.target.value }))}
                  placeholder="https://apps.apple.com/app/id…"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AppleIcon sx={{ fontSize: 18 }} />
                      </InputAdornment>
                    )
                  }}
                />
                <TextField
                  fullWidth size="small"
                  label="Google Play (Android)"
                  value={notifForm.androidUrl}
                  onChange={e => setNotifForm(p => ({ ...p, androidUrl: e.target.value }))}
                  placeholder="https://play.google.com/store/apps/details?id=…"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon sx={{ fontSize: 18, color: '#3ddc84' }} />
                      </InputAdornment>
                    )
                  }}
                />
              </>
            )}

            {/* Info del usuario */}
            {notifDialog.user && (() => {
              const tokens = (notifDialog.user.expoTokens || []).filter(t => t.active && t.token);
              const ios     = tokens.filter(t => t.deviceInfo?.platform === 'ios').length;
              const android = tokens.filter(t => t.deviceInfo?.platform === 'android').length;
              return (
                <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    <PhoneIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                    {tokens.length} token(s) activo(s)
                    {ios > 0 && ` · iOS: ${ios}`}
                    {android > 0 && ` · Android: ${android}`}
                    {` · versión ${notifDialog.user.appVersion || 'desconocida'}`}
                  </Typography>
                </Box>
              );
            })()}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNotifDialog({ open: false, user: null })} disabled={sending}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            onClick={handleSend}
            disabled={sending || !notifForm.title.trim()}
          >
            {sending ? 'Enviando…' : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000}
        onClose={() => setSnackbar(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(p => ({ ...p, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AppVersions;
